class SheetManager {
	constructor() {
		this.sheets = new Map(); // Map of sheet instances
		this.stack = []; // Array of sheet IDs in stack order (bottom to top)
		this.baseZIndex = 9999900;
		// this.ensurePortalTargets();
	}

	// Ensure portal targets exist in DOM
	ensurePortalTargets() {
		if (typeof document === 'undefined') return; // SSR safety

		if (!document.getElementById('bottom-sheet-stack')) {
			const stackTarget = document.createElement('div');
			stackTarget.id = 'bottom-sheet-stack';
			stackTarget.className = 'portal-target portal-target--stack';
			document.body.appendChild(stackTarget);
		}

		if (!document.getElementById('bottom-sheet')) {
			const defaultTarget = document.createElement('div');
			defaultTarget.id = 'bottom-sheet';
			defaultTarget.className = 'portal-target portal-target--default';
			document.body.appendChild(defaultTarget);
		}
	}

	register(sheetId, instance) {
		this.sheets.set(sheetId, instance);
		console.log(`Sheet registered: ${sheetId}`);
	}

	unregister(sheetId) {
		const wasInStack = this.stack.includes(sheetId);
		this.sheets.delete(sheetId);
		this.removeFromStack(sheetId);
		console.log(`Sheet unregistered: ${sheetId}${wasInStack ? ' (was in stack)' : ''}`);
	}

	addToStack(sheetId, behavior = 'replace') {
		const sheet = this.sheets.get(sheetId);
		if (!sheet) {
			console.warn(`Attempted to add non-existent sheet to stack: ${sheetId}`);
			return;
		}

		if (behavior === 'replace') {
			// Close all existing sheets first, but don't emit close events
			this.stack.forEach(id => {
				if (id !== sheetId) {
					const existingSheet = this.sheets.get(id);
					if (existingSheet) {
						existingSheet.forceClose(); // Internal close without emit
					}
				}
			});
			this.stack = [sheetId];
		} else if (behavior === 'stack') {
			// Stack behavior - keep existing sheets open
			if (!this.stack.includes(sheetId)) {
				this.stack.push(sheetId);
			} else {
				// Move to top if already in stack
				this.removeFromStack(sheetId);
				this.stack.push(sheetId);
			}
		}

		this.updateZIndexes();
		console.log(`Stack updated (${behavior}):`, this.stack);
	}

	removeFromStack(sheetId) {
		const index = this.stack.indexOf(sheetId);
		if (index > -1) {
			this.stack.splice(index, 1);
			this.updateZIndexes();
			console.log(`Sheet removed from stack: ${sheetId}. Remaining:`, this.stack);
		}
	}

	updateZIndexes() {
		this.stack.forEach((sheetId, index) => {
			const sheet = this.sheets.get(sheetId);
			if (sheet) {
				const zIndex = this.baseZIndex + (index * 2);
				sheet.updateZIndex(zIndex);
				console.log(`Sheet ${sheetId} z-index updated to: ${zIndex}`);
			}
		});
	}

	getTopSheet() {
		return this.stack.length > 0 ? this.stack[this.stack.length - 1] : null;
	}

	isTopSheet(sheetId) {
		return this.getTopSheet() === sheetId;
	}

	getStackPosition(sheetId) {
		return this.stack.indexOf(sheetId);
	}

	closeAllAbove(sheetId) {
		const index = this.stack.indexOf(sheetId);
		if (index > -1) {
			const sheetsToClose = this.stack.slice(index + 1);
			sheetsToClose.forEach(id => {
				const sheet = this.sheets.get(id);
				if (sheet) {
					sheet.close(); // This will emit close event
				}
			});
		}
	}

	// Close all sheets
	closeAll() {
		[...this.stack].forEach(sheetId => {
			const sheet = this.sheets.get(sheetId);
			if (sheet) {
				sheet.close();
			}
		});
	}

	// Get current stack info (useful for debugging)
	getStackInfo() {
		return {
			stackSize: this.stack.length,
			topSheet: this.getTopSheet(),
			allSheets: Array.from(this.sheets.keys()),
			stackOrder: [...this.stack]
		};
	}

	// Handle layout changes (useful if sheets need to adapt)
	handleLayoutChange(newLayout) {
		console.log(`Layout changed to: ${newLayout}`);
		// Notify all sheets about layout change if needed
		this.sheets.forEach((sheet, sheetId) => {
			if (typeof sheet.handleLayoutChange === 'function') {
				sheet.handleLayoutChange(newLayout);
			}
		});
	}

	// Clean up method
	destroy() {
		this.closeAll();
		this.sheets.clear();
		this.stack = [];

		// Remove portal targets if they were created by this manager
		const stackTarget = document.getElementById('bottom-sheet-stack');
		const defaultTarget = document.getElementById('bottom-sheet');

		if (stackTarget && stackTarget.className.includes('portal-target--stack')) {
			stackTarget.remove();
		}

		if (defaultTarget && defaultTarget.className.includes('portal-target--default')) {
			defaultTarget.remove();
		}
	}
}

// Create global instance
const sheetManager = new SheetManager();

// Add global CSS for portal targets
if (typeof document !== 'undefined') {
	const style = document.createElement('style');
	style.textContent = `
    .portal-target {
      pointer-events: none;
      position: relative;
      z-index: 0;
    }

    .portal-target > * {
      pointer-events: auto;
    }
  `;
	document.head.appendChild(style);
}

export default sheetManager;
